/**
 * Subscription / billing service for the Subscriptions page and Account.
 *
 * Reads plan + usage from user_subscriptions / v_unit_balance (Stripe webhook writes).
 * Checkout and the billing portal go through the billing-gateway edge function, which
 * holds the worker secret and checks that the signed-in user owns the domain.
 */

import { supabase } from "@/lib/supabaseClient";
import {
  type BoosterPack,
  type RescoreProduct,
  type RemediationTier,
  type Currency,
} from "./remediationPricing";

export interface SubscriptionStatus {
  tier: string | null;
  status: "active" | "inactive" | "past_due" | "canceled" | "incomplete";
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  nextBillingDate?: string;
  cancelAtPeriodEnd?: boolean;
  cancelAt?: string;
  canceledAt?: string;
  /** Scheduled service end; active access continues until this date. */
  cancellationDate?: string;
  monthlyUnits?: number;
  includedSites?: number;
  includedUrls?: number;
  currency?: string;
}

export interface UsageStatus {
  /** Monthly units still available this cycle (from non-expired grants − debits share). */
  included: number;
  /** Units consumed (debit_fix total). */
  used: number;
  /** Booster units still available (persist until used). */
  boosterRemaining: number;
  /** Total spendable balance (monthly remaining + boosters). */
  balance: number;
  /** ISO date the monthly allowance resets. */
  resetsOn?: string;
}

export type CheckoutResult =
  | { ok: true; url: string }
  | {
      ok: false;
      reason: "full_required" | "subscription_exists" | "not_configured" | "no_active_tier" | "domain_required" | "domain_not_owned" | "error";
    };

/** Billing dates on Account and Subscriptions, e.g. "6 Nov 2026". */
export function formatBillingDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Context stamped onto the checkout session so the webhook/claim can link the purchase to a user. */
export interface CheckoutContext {
  userId?: string;
  domain?: string;
  successUrl?: string;
  cancelUrl?: string;
}

/** Resolve the buyer's regional price book through the authenticated gateway. */
export async function getCheckoutCurrency(): Promise<Currency | null> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      supabase.functions.invoke("billing-gateway", { body: { action: "pricing_context" } })
        .then(({ data, error }): Currency | null => {
          if (error || data?.ok !== true) return null;
          return data.currency === "AUD" || data.currency === "USD" ? data.currency : null;
        }),
      new Promise<null>((resolve) => { timeout = setTimeout(() => resolve(null), 8000); }),
    ]);
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Current remediation subscription for the signed-in user.
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatus | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from("user_subscriptions")
    .select(
      "tier, status, current_period_start, current_period_end, monthly_units, included_sites, included_urls, currency, cancel_at_period_end, cancel_at, canceled_at"
    )
    .eq("user_id", user.id)
    .in("status", ["active", "past_due", "incomplete"])
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.warn("[subscriptionService] getSubscriptionStatus:", error.message);
    return null;
  }
  if (!data) {
    return { tier: null, status: "inactive" };
  }

  const cancellationDate = data.cancel_at || (data.cancel_at_period_end ? data.current_period_end : undefined);
  return {
    tier: data.tier,
    status: (data.status || "inactive") as SubscriptionStatus["status"],
    currentPeriodStart: data.current_period_start || undefined,
    currentPeriodEnd: data.current_period_end || undefined,
    nextBillingDate: cancellationDate ? undefined : data.current_period_end || undefined,
    cancelAtPeriodEnd: Boolean(data.cancel_at_period_end),
    cancelAt: data.cancel_at || undefined,
    canceledAt: data.canceled_at || undefined,
    cancellationDate: cancellationDate || undefined,
    monthlyUnits: data.monthly_units,
    includedSites: data.included_sites,
    includedUrls: data.included_urls,
    currency: data.currency || undefined,
  };
}

/**
 * Remediation unit usage for the current cycle (from v_unit_balance).
 */
export async function getUsage(): Promise<UsageStatus | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from("v_unit_balance")
    .select("monthly_granted, booster_granted, used, balance")
    .eq("user_id", user.id);

  if (error) {
    console.warn("[subscriptionService] getUsage:", error.message);
    return null;
  }
  if (!data?.length) {
    return {
      included: 0,
      used: 0,
      boosterRemaining: 0,
      balance: 0,
    };
  }

  // View groups by (user_id, stripe_customer_id) — sum if multiple owner keys
  const monthlyGranted = data.reduce((s, r) => s + (Number(r.monthly_granted) || 0), 0);
  const boosterGranted = data.reduce((s, r) => s + (Number(r.booster_granted) || 0), 0);
  const used = data.reduce((s, r) => s + (Number(r.used) || 0), 0);
  const balance = data.reduce((s, r) => s + (Number(r.balance) || 0), 0);

  // Approximate remaining monthly vs booster: monthly is consumed first.
  const monthlyRemaining = Math.max(0, monthlyGranted - Math.min(used, monthlyGranted));
  const boosterUsed = Math.max(0, used - monthlyGranted);
  const boosterRemaining = Math.max(0, boosterGranted - boosterUsed);

  const sub = await getSubscriptionStatus();

  return {
    included: monthlyRemaining,
    used,
    boosterRemaining,
    balance,
    resetsOn: sub?.currentPeriodEnd,
  };
}

/** True when the user has spendable remediation units. */
export async function hasUnitsAvailable(): Promise<boolean> {
  const usage = await getUsage();
  return Boolean(usage && usage.balance > 0);
}

/**
 * Whether Ruby may generate a fix.
 * Docs: gate generation when v_unit_balance.balance = 0
 * (no plan / no units → blocked; prompt subscribe or buy booster).
 */
export async function canGenerateFix(): Promise<boolean> {
  return hasUnitsAvailable();
}

/** True when the user has an active (or past_due) remediation tier — required before boosters. */
export async function hasActiveTier(): Promise<boolean> {
  const status = await getSubscriptionStatus();
  return Boolean(status && (status.status === "active" || status.status === "past_due") && status.tier);
}

async function readProxyReason(error: unknown): Promise<string | undefined> {
  const response = (error as { context?: { json?: () => Promise<{ reason?: string; error?: string }> } })?.context;
  if (!response?.json) return undefined;
  const payload = await response.json().catch(() => null);
  return payload?.reason || payload?.error;
}

function asCheckoutFailure(reason?: string): CheckoutResult {
  if (reason === "not_configured" || reason === "billing_not_configured") {
    return { ok: false, reason: "not_configured" };
  }
  if (reason === "full_required" || reason === "subscription_exists" || reason === "no_active_tier" || reason === "domain_required" || reason === "domain_not_owned") {
    return { ok: false, reason };
  }
  return { ok: false, reason: "error" };
}

/**
 * Calls billing-gateway with the current session JWT.
 * The proxy checks domain ownership, then calls the worker with the server secret.
 */
async function invokeBillingProxy(body: Record<string, unknown>): Promise<CheckoutResult> {
  try {
    const { data, error } = await supabase.functions.invoke("billing-gateway", { body });
    const url = (data as { url?: string } | null)?.url;
    if (url) return { ok: true, url };
    const reason = (data as { reason?: string } | null)?.reason || (error ? await readProxyReason(error) : undefined);
    return asCheckoutFailure(reason);
  } catch {
    return { ok: false, reason: "error" };
  }
}

/**
 * Starts a Stripe Checkout for a NEW tier subscription (Starter / Growth / Scale).
 * Only for users without an active tier — an existing subscriber's plan change goes through the
 * Billing Portal (openBillingPortal), not a second checkout.
 * The worker owns subscription creation, pricing, and the Full Scoring eligibility rule; the
 * frontend just routes and surfaces the rejection.
 */
export async function startTierCheckout(
  tier: RemediationTier,
  currency: Currency,
  ctx: CheckoutContext = {}
): Promise<CheckoutResult> {
  if (!tier.productCode) return { ok: false, reason: "not_configured" };
  if (!ctx.domain) return { ok: false, reason: "domain_required" };

  return invokeBillingProxy({
    action: "checkout",
    product: tier.productCode,
    domain: ctx.domain,
    currency,
  });
}

/**
 * Starts a Stripe checkout for a booster pack.
 * Requires an active remediation tier (og01.io gate) and a domain on the account.
 * Currency is forwarded to the authenticated gateway; the worker selects its fixed Price.
 */
export async function startBoosterCheckout(
  pack: BoosterPack,
  currency: Currency,
  ctx: CheckoutContext = {}
): Promise<CheckoutResult> {
  if (!pack.productCode) return { ok: false, reason: "not_configured" };
  if (!ctx.domain) return { ok: false, reason: "domain_required" };

  const okTier = await hasActiveTier();
  if (!okTier) return { ok: false, reason: "no_active_tier" };

  return invokeBillingProxy({
    action: "checkout",
    product: pack.productCode,
    domain: ctx.domain,
    currency,
  });
}

/**
 * Starts a Stripe checkout for Rescore Full.
 * Requires an active remediation tier. The worker stamps this caller as og01.io.
 */
export async function startRescoreCheckout(
  product: RescoreProduct,
  currency: Currency,
  ctx: CheckoutContext = {}
): Promise<CheckoutResult> {
  if (!product.productCode) return { ok: false, reason: "not_configured" };
  if (!ctx.domain) return { ok: false, reason: "domain_required" };

  const okTier = await hasActiveTier();
  if (!okTier) return { ok: false, reason: "no_active_tier" };

  return invokeBillingProxy({
    action: "checkout",
    product: product.productCode,
    domain: ctx.domain,
    currency,
  });
}

/** Opens the Stripe Billing Portal for a domain this account owns. */
export async function openBillingPortal(params: {
  domain?: string;
  returnUrl?: string;
} = {}): Promise<CheckoutResult> {
  return invokeBillingProxy({
    action: "portal",
    domain: params.domain,
    return_url: params.returnUrl,
  });
}

